import { useState, useEffect } from "react";
import apiFetch from "../../../../shared/services/apiClient";

/**
 * useReceipts
 *
 * Acepta un objeto `params` con los filtros activos y los pasa al backend
 * como query string. El backend hace toda la búsqueda/filtrado/ordenamiento
 * en DB — nada se procesa en el cliente.
 *
 * params: {
 *   search, fecha_desde, fecha_hasta, monto_min, monto_max,
 *   metodo_pago (array), cuenta_bancaria (array), ordering
 * }
 *
 * Cuando params o page cambian se hace un nuevo fetch.
 * Se incluye un debounce de 300ms en search para no disparar una petición
 * por cada tecla.
 */
export function useReceipts(page = 1, params = {}, refreshKey = 0) {
  const [receipts, setReceipts] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // Debounce solo para el campo search — evita llamadas por cada tecla
    const delay = params.search ? 300 : 0;

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const qs = buildQueryString(page, params);
        const res = await apiFetch(`/api/finance/recibo/?${qs}`);
        const data = await res.json();
        if (!cancelled) {
          setReceipts(data.results ?? []);
          setCount(data.count ?? 0);
          setNext(data.next);
          setPrevious(data.previous);
        }
      } catch {
        // silenciar errores de red transitorios
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, refreshKey, JSON.stringify(params)]);

  return { receipts, count, next, previous, loading };
}

/** Construye el query string a partir del objeto params */
function buildQueryString(page, params) {
  const p = new URLSearchParams();
  p.set("page", String(page));

  if (params.search?.trim()) p.set("search", params.search.trim());
  if (params.fecha_desde?.trim())
    p.set("fecha_desde", params.fecha_desde.trim());
  if (params.fecha_hasta?.trim())
    p.set("fecha_hasta", params.fecha_hasta.trim());
  if (params.monto_min?.trim()) p.set("monto_min", params.monto_min.trim());
  if (params.monto_max?.trim()) p.set("monto_max", params.monto_max.trim());
  if (params.ordering?.trim()) p.set("ordering", params.ordering.trim());

  // Arrays — se pueden repetir: metodo_pago=EFECTIVO&metodo_pago=CHEQUE
  (params.metodo_pago ?? []).forEach((m) => p.append("metodo_pago", m));
  (params.cuenta_bancaria ?? []).forEach((c) =>
    p.append("cuenta_bancaria", String(c)),
  );

  return p.toString();
}

/** Recibos sin ningún DetalleRecibo aplicado */
export function usePendingReceipts(refreshKey = 0) {
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch("/api/finance/recibo/pendientes/")
      .then((r) => r.json())
      .then((data) => {
        setPending(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [refreshKey]);

  return { pending, loading };
}

/** Cuentas bancarias del condominio para el panel de filtros */
export function useCuentasFiltro() {
  const [cuentas, setCuentas] = useState([]);

  useEffect(() => {
    apiFetch("/api/finance/recibo/cuentas-filtro/")
      .then((r) => r.json())
      .then((data) => setCuentas(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  return cuentas;
}
